import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, StarIcon, XIcon } from 'lucide-react';

/**
 * Self-contained "Look Inside" carousel for Bollywood Class Ready, Volume 6.
 * Everything this block needs (pages, captions, lightbox, background motif) lives
 * in this one file so it can be dropped into any landing page.
 * Requires: react, framer-motion, lucide-react, Tailwind and the Manrope font.
 */

type Page = {
  src: string;
  caption: string;
};
const pages: Page[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/iknxtGSqpBauzhmyz7sQvQ/01_T6-pag-01.png',
  caption: 'Volume 6: Showcases, Formations & Your Teaching Year'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/6uSAg73hGYcmRmUdhHPGJS/02_T6-pag-06.png',
  caption: 'The Year Map: three terms, shows and breaks on one page'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/h1VC3CGLZxbzZaDTd3Yevy/03_T6-pag-16.png',
  caption: 'Ready-made formations for 10 to 20 dancers'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/icRrfCARWcVJakwht1Md7G/04_T6-pag-21.png',
  caption: "What works vs. what doesn't, shown side by side"
}, {
  src: 'https://cdn.magicpatterns.com/uploads/dwczsimWfRiRAiTMzvwMea/05_T6-pag-48.png',
  caption: 'Your whole teaching year at a glance'
}];
const ease = [0.23, 1, 0.32, 1] as const;
export function LookInside() {
  const trackRef = useRef<HTMLUListElement>(null);
  const slideRefs = useRef<(HTMLLIElement | null)[]>([]);
  const frameRef = useRef<number | null>(null);
  const [active, setActive] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const reduceMotion = useReducedMotion();
  const count = pages.length;
  const scrollToIndex = useCallback((i: number) => {
    const track = trackRef.current;
    const slide = slideRefs.current[i];
    if (!track || !slide) return;
    const left = slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2;
    track.scrollTo({
      left,
      behavior: reduceMotion ? 'auto' : 'smooth'
    });
  }, [reduceMotion]);
  const goTo = useCallback((i: number) => {
    const next = Math.max(0, Math.min(count - 1, i));
    setActive(next);
    scrollToIndex(next);
  }, [count, scrollToIndex]);
  const onScroll = () => {
    if (frameRef.current !== null) return;
    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = null;
      const track = trackRef.current;
      if (!track) return;
      const center = track.scrollLeft + track.clientWidth / 2;
      let nearest = 0;
      let best = Infinity;
      slideRefs.current.forEach((slide, i) => {
        if (!slide) return;
        const distance = Math.abs(slide.offsetLeft + slide.clientWidth / 2 - center);
        if (distance < best) {
          best = distance;
          nearest = i;
        }
      });
      setActive(nearest);
    });
  };

  // Keep the active slide centered when the viewport resizes.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const ro = new ResizeObserver(() => {
      const slide = slideRefs.current[active];
      if (!slide) return;
      track.scrollLeft = slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2;
    });
    ro.observe(track);
    return () => ro.disconnect();
  }, [active]);
  useEffect(() => () => {
    if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current);
  }, []);
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goTo(active - 1);
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      goTo(active + 1);
    }
  };
  const onSlideClick = (i: number) => {
    if (i !== active) {
      goTo(i);
      return;
    }
    setLightboxIndex(i);
  };
  const arrowBtn = 'absolute top-1/2 z-10 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-[#FF6B35] text-white shadow-[0_8px_20px_-8px_rgba(255,107,53,0.7)] transition-[background-color,opacity,transform] duration-150 ease-out hover:bg-[#F2551C] active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#FF6B35]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FBF7F2] disabled:pointer-events-none disabled:opacity-30 sm:flex';
  return <section aria-labelledby="peek-inside-heading" className="relative w-full overflow-hidden bg-[#FBF7F2] py-16 font-['Manrope',system-ui,sans-serif] text-[#5A5A5A] sm:py-24" data-id="element-141" data-mp="368a306rpz0mi">
      <MandalaMotif data-id="element-142" data-mp="2wbx0qki3zeg5" />

      <div className="relative mx-auto max-w-5xl" data-id="element-143" data-mp="2wcq91pwpuc0e">
        <div className="px-6 text-center" data-id="element-144" data-mp="319ysruxiio3r">
          <span className="inline-flex items-center rounded-full bg-[#C8124A] px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-white" data-id="element-145" data-mp="31b01vtuyx0iu">
            Peek inside Volume 6
          </span>
          <h2 id="peek-inside-heading" className="mx-auto mt-5 max-w-[22ch] text-[28px] font-semibold leading-[1.15] tracking-[-0.02em] text-[#1A1A1A] sm:max-w-[26ch] sm:text-[40px] sm:leading-[1.1]" data-id="element-146" data-mp="2s90odbj5wekn">
            Plan the year. Stage the show. Every page ready to use.
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed sm:text-[17px]" data-id="element-147" data-mp="2hmc42wju2gmd">
            Swipe through real pages from inside the pack.
          </p>
        </div>

        <div className="relative mt-10 rounded-3xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8124A]/50 sm:mt-12" role="region" aria-roledescription="carousel" aria-label="Pages from Volume 6" tabIndex={0} onKeyDown={onKeyDown} data-id="element-148" data-mp="2rlkvbhnbdh51">
          <ul ref={trackRef} onScroll={onScroll} className="relative flex snap-x snap-mandatory gap-3 overflow-x-auto px-[12%] py-6 [scrollbar-width:none] sm:gap-6 sm:px-[calc(50%-160px)] lg:px-[calc(50%-180px)] [&::-webkit-scrollbar]:hidden" data-id="element-149" data-mp="2wdupxazdj5dn">
            {pages.map((page, i) => {
            const isActive = i === active;
            return <li key={page.src} ref={el => {
              slideRefs.current[i] = el;
            }} className="w-[76%] flex-none snap-center sm:w-[320px] lg:w-[360px]" aria-roledescription="slide" aria-label={`${i + 1} of ${count}`} data-id="element-150" data-mp="2807ko5nfph68" data-mpi={i}>
                  <button type="button" onClick={() => onSlideClick(i)} tabIndex={isActive ? 0 : -1} aria-label={isActive ? `Open full screen: ${page.caption}` : `Show page ${i + 1}: ${page.caption}`} className={`block w-full overflow-hidden rounded-2xl bg-white shadow-[0_18px_40px_-18px_rgba(26,26,26,0.35),0_4px_12px_rgba(26,26,26,0.06)] transition-[transform,opacity] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#C8124A]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#FBF7F2] ${isActive ? 'scale-100 cursor-zoom-in opacity-100' : 'scale-[0.94] cursor-pointer opacity-[0.55]'}`} data-id="element-151" data-mp="2qpnytvs1l854">
                    <img src={page.src} alt={page.caption} width={1080} height={1920} loading={i < 2 ? 'eager' : 'lazy'} decoding="async" draggable={false} className="aspect-[9/16] w-full select-none object-contain" data-id="element-152" data-mp="k387l6rq0w82" />
                  </button>
                </li>;
          })}
          </ul>

          <button type="button" onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Previous page" className={`${arrowBtn} left-4 lg:left-10`} data-id="element-153" data-mp="3sii4m0gr7b8z">
            <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-154" data-mp="1rhlm1t0tpcvt" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} disabled={active === count - 1} aria-label="Next page" className={`${arrowBtn} right-4 lg:right-10`} data-id="element-155" data-mp="1vif9klacspo6">
            <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-156" data-mp="2a68p65zfvs6r" />
          </button>
        </div>

        <div className="mt-4 flex min-h-[48px] items-start justify-center px-6 text-center" aria-live="polite" data-id="element-157" data-mp="2f5leqyhp3dd1">
          <AnimatePresence mode="wait" initial={false} data-id="element-158" data-mp="20gyzews2illt">
            <motion.p key={active} initial={reduceMotion ? {
            opacity: 0
          } : {
            opacity: 0,
            y: 4
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={reduceMotion ? {
            opacity: 0
          } : {
            opacity: 0,
            y: -4
          }} transition={{
            duration: 0.15,
            ease
          }} className="max-w-[34ch] text-[15px] font-semibold leading-snug text-[#1A1A1A] sm:text-base" data-id="element-159" data-mp="20ecrux56g63l">
              {pages[active].caption}
            </motion.p>
          </AnimatePresence>
        </div>

        <div className="mt-5 flex items-center justify-center gap-4" data-id="element-160" data-mp="v8o10248v9qz">
          <div className="flex items-center" role="group" aria-label="Choose page" data-id="element-161" data-mp="vb0eo32yyxy8">
            {pages.map((page, i) => <button key={page.src} type="button" onClick={() => goTo(i)} aria-label={`Go to page ${i + 1}: ${page.caption}`} aria-current={active === i ? 'true' : undefined} className="flex h-7 w-7 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8124A]/50" data-id="element-162" data-mp="3hgj3conpm0v" data-mpi={i}>
                <span className={`block h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${active === i ? 'w-6 bg-[#C8124A]' : 'w-2 bg-[#D9D3CB]'}`} data-id="element-163" data-mp="1nwjvr1a96rl4" />
              </button>)}
          </div>
          <p className="text-sm font-semibold tabular-nums text-[#5A5A5A]" aria-hidden="true" data-id="element-164" data-mp="325rtr46chacy">
            <span className="text-[#FF6B35]" data-id="element-165" data-mp="2xcyn2arbfry8">{active + 1}</span> / {count}
          </p>
        </div>

        <p className="mt-6 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 px-6 text-center text-[13px] text-[#5A5A5A]" data-id="element-166" data-mp="2tbdgf75dk9wh">
          <StarIcon className="h-3.5 w-3.5 fill-[#F5B83D] text-[#F5B83D]" aria-hidden="true" data-id="element-167" data-mp="2oen1in5b3kcn" />
          <span data-id="element-168" data-mp="2jcxlsu752ii8">50 ready-to-use pages in this volume</span>
          <span aria-hidden="true" className="text-[#C9C2B8]" data-id="element-169" data-mp="2eee0mw0ddaqx">·</span>
          <span data-id="element-170" data-mp="2adkd43qu9xyk">6 volumes in the full pack</span>
        </p>
      </div>

      <PageLightbox index={lightboxIndex} onClose={() => setLightboxIndex(null)} onChange={i => {
      setLightboxIndex(i);
      goTo(i);
    }} data-id="element-171" data-mp="2afdizgtmoe5i" />
    </section>;
}
type PageLightboxProps = {
  index: number | null;
  onClose: () => void;
  onChange: (index: number) => void;
};
function PageLightbox({
  index,
  onClose,
  onChange
}: PageLightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const open = index !== null;
  const count = pages.length;
  useEffect(() => {
    if (!open) return;
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
      returnFocusRef.current?.focus();
    };
  }, [open]);
  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && index > 0) onChange(index - 1);
      if (e.key === 'ArrowRight' && index < count - 1) onChange(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, count, onClose, onChange]);
  const page = index !== null ? pages[index] : null;
  const navBtn = 'absolute top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white transition-colors duration-150 hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:pointer-events-none disabled:opacity-30 sm:flex';
  return <AnimatePresence data-id="element-172" data-mp="98km3dmmyfjm">
      {open && page && <motion.div role="dialog" aria-modal="true" aria-label={page.caption} className="fixed inset-0 z-[60] flex items-center justify-center bg-[#1A1A1A]/85 px-4 py-6 font-['Manrope',system-ui,sans-serif]" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} onClick={onClose} data-id="element-173" data-mp="99vp3b89bj9o">
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close full screen view" className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white transition-colors duration-150 hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-174" data-mp="2256h2z94h4wx">
            <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-175" data-mp="2kuwcnokplupw" />
          </button>

          <button type="button" onClick={e => {
        e.stopPropagation();
        if (index! > 0) onChange(index! - 1);
      }} disabled={index === 0} aria-label="Previous page" className={`${navBtn} left-6`} data-id="element-176" data-mp="2puimrp58quuh">
            <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-177" data-mp="10fkoabeq7pw1" />
          </button>

          <motion.figure key={page.src} className="flex max-h-full flex-col items-center" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.96
      }} transition={{
        duration: 0.22,
        ease
      }} onClick={e => e.stopPropagation()} data-id="element-178" data-mp="10hx8ywbvcecs">
            <img src={page.src} alt={page.caption} width={1080} height={1920} className="max-h-[84vh] w-auto max-w-[92vw] rounded-2xl bg-white object-contain shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)]" data-id="element-179" data-mp="1ayr4055n06rg" />
            <figcaption className="mt-3 max-w-[40ch] text-center text-sm text-white/90" data-id="element-180" data-mp="1ypdb9u7thn0e">
              {page.caption}
              <span className="ml-2 tabular-nums text-white/60" data-id="element-181" data-mp="1ukniie8ggojp">
                {index! + 1} / {count}
              </span>
            </figcaption>
          </motion.figure>

          <button type="button" onClick={e => {
        e.stopPropagation();
        if (index! < count - 1) onChange(index! + 1);
      }} disabled={index === count - 1} aria-label="Next page" className={`${navBtn} right-6`} data-id="element-182" data-mp="j835l0vclfhh">
            <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-183" data-mp="2uuwvwifalr0f" />
          </button>
        </motion.div>}
    </AnimatePresence>;
}

/** Faint paisley-mandala line art for the top-right corner. Purely decorative. */
function MandalaMotif() {
  return <svg aria-hidden="true" viewBox="0 0 200 200" fill="none" stroke="currentColor" strokeWidth="1.1" className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 text-[#C8124A] opacity-[0.05] sm:-right-16 sm:-top-16 sm:h-[420px] sm:w-[420px]" data-id="element-184" data-mp="38mux872riim1">
      <circle cx="100" cy="100" r="14" data-id="element-185" data-mp="3mm4ela2pfirl" />
      <circle cx="100" cy="100" r="24" data-id="element-186" data-mp="3ml1o339anic7" />
      <circle cx="100" cy="100" r="62" data-id="element-187" data-mp="1o6hx0n7xvn9e" />
      <circle cx="100" cy="100" r="94" data-id="element-188" data-mp="1o7jhfiauhm3s" />
      {Array.from({
      length: 12
    }).map((_, i) => <path key={`petal-${i}`} d="M100 76 C 90 60, 90 46, 100 34 C 110 46, 110 60, 100 76 Z" transform={`rotate(${i * 30} 100 100)`} data-id="element-189" data-mp="1s8l9nf6cgxw9" data-mpi={i} />)}
      {Array.from({
      length: 12
    }).map((_, i) => <path key={`paisley-${i}`} d="M100 36 C 112 26, 112 14, 100 8 C 92 12, 92 22, 100 24" transform={`rotate(${i * 30 + 15} 100 100)`} data-id="element-190" data-mp="2qlqxoxmgk1u5" data-mpi={i} />)}
      {Array.from({
      length: 36
    }).map((_, i) => {
      const a = i * 10 * Math.PI / 180;
      return <circle key={`dot-${i}`} cx={100 + Math.cos(a) * 78} cy={100 + Math.sin(a) * 78} r="1.6" data-id="element-191" data-mp="3efqx723l8awt" data-mpi={i} />;
    })}
    </svg>;
}